import * as React from 'react';

import type { Extension } from '@codemirror/state';
import { useLsp } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  $editorEdges,
  $editorNodes,
  $selectedNode,
} from '../../../_state/automationEditor.state';

import { computeScopeSchema } from './scopeComputation';

/**
 * Hook that manages the LSP context for the automations editor.
 *
 * Automatically updates the LSP server with the scope schema when:
 * - The selected node changes
 * - The workflow structure changes (nodes/edges)
 *
 * @returns Object with getExtensions function and isReady state
 */
export function useAutomationLspContext(): {
  /**
   * Get CodeMirror extensions for a document.
   * Returns empty array if LSP is not ready.
   *
   * @param documentUri - Unique URI for this document
   * @param languageId - Optional language ID (defaults to 'expr')
   */
  getExtensions: (documentUri: string, languageId?: string) => Extension;
  /**
   * Whether the LSP client is ready
   */
  isReady: boolean;
} {
  const { updateContext, getExtensions, isReady } = useLsp();

  const selectedNode = useAtomValue($selectedNode);
  const nodes = useAtomValue($editorNodes);
  const edges = useAtomValue($editorEdges);

  // Track the last computed context to avoid duplicate updates
  const lastContextRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!selectedNode || !isReady) return;

    // Create a cache key based on relevant state
    const cacheKey = JSON.stringify({
      selectedNodeId: selectedNode.id,
      nodeTypes: nodes.map((n) => ({
        id: n.id,
        type: n.type,
        configCase:
          n.type === 'BusinessEventTrigger'
            ? (n.data.config as { source?: { case?: string } })?.source?.case
            : undefined,
      })),
      edgeCount: edges.length,
    });

    // Skip if context hasn't changed
    if (lastContextRef.current === cacheKey) {
      return;
    }
    lastContextRef.current = cacheKey;

    // Compute scope schema (synchronous - all schemas are pre-loaded via import.meta.glob)
    try {
      const schema = computeScopeSchema(selectedNode.id, nodes, edges);

      updateContext({
        schema,
        documentUri: `expr://automation/${selectedNode.id}`,
      });
    } catch (error) {
      console.error('Failed to compute scope schema:', error);
    }
  }, [selectedNode, nodes, edges, isReady, updateContext]);

  // Memoize the getExtensions wrapper to ensure stable reference
  const wrappedGetExtensions = React.useCallback(
    (documentUri: string, languageId?: string): Extension =>
      getExtensions(documentUri, languageId),
    [getExtensions],
  );

  return React.useMemo(
    () => ({
      getExtensions: wrappedGetExtensions,
      isReady,
    }),
    [wrappedGetExtensions, isReady],
  );
}

/**
 * Generate a document URI for a node's expression input.
 *
 * @param nodeId - The ID of the node
 * @param fieldName - Optional field name for multiple expression inputs per node
 * @returns Document URI string
 */
export function getExpressionDocumentUri(
  nodeId: string,
  fieldName?: string,
): string {
  return fieldName
    ? `expr://automation/${nodeId}/${fieldName}`
    : `expr://automation/${nodeId}`;
}
